/**
 * Logger Utility for Temporal Parking Algorithm
 *
 * Provides environment-aware logging with multiple severity levels.
 * Defaults to INFO level to prevent performance issues from verbose DEBUG logs.
 *
 * Usage:
 * ```typescript
 * import { logger } from './utils/logger';
 *
 * logger.debug('Detailed debug information', { data });
 * logger.info('General information', value);
 * logger.warn('Warning message', issue);
 * logger.error('Error occurred', error);
 * ```
 *
 * To enable DEBUG logs (for troubleshooting):
 * ```typescript
 * // In browser console or at app startup:
 * logger.configure({ minLevel: 'DEBUG' });
 * ```
 */

export type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';

/**
 * Determines if we're running in development mode
 * Uses NODE_ENV or defaults to production for performance
 */
function isDevelopment(): boolean {
  try {
    return typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development';
  } catch (e) {
    return false;
  }
}

/**
 * Logger configuration
 */
interface LoggerConfig {
  /** Minimum log level to display (defaults based on environment) */
  minLevel: LogLevel;
  /** Enable/disable all logging */
  enabled: boolean;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
};

/**
 * Default configuration
 * - Always defaults to INFO level to avoid performance issues
 * - Can be changed at runtime with logger.configure({ minLevel: 'DEBUG' })
 */
const defaultConfig: LoggerConfig = {
  minLevel: 'INFO',
  enabled: true,
};

let config: LoggerConfig = { ...defaultConfig };

/**
 * Format log prefix with level and optional tag
 */
function formatPrefix(level: LogLevel, tag?: string): string {
  try {
    const timestamp = new Date().toISOString().split('T')[1].slice(0, -1); // HH:MM:SS.mmm
    const tagStr = tag ? ` [${tag}]` : '';
    return `[${timestamp}] [${level}]${tagStr}`;
  } catch (e) {
    // Fallback if timestamp formatting fails
    const tagStr = tag ? ` [${tag}]` : '';
    return `[${level}]${tagStr}`;
  }
}

/**
 * Check if a log level should be displayed
 */
function shouldLog(level: LogLevel): boolean {
  if (!config.enabled) return false;
  return LOG_LEVELS[level] >= LOG_LEVELS[config.minLevel];
}

/**
 * Logger implementation
 */
export const logger = {
  /**
   * Debug-level logging (verbose, detailed information)
   * Suppressed in production environments
   *
   * Use for: Algorithm internals, detailed state, trace information
   */
  debug(message: string, ...args: any[]): void {
    if (shouldLog('DEBUG')) {
      console.log(formatPrefix('DEBUG'), message, ...args);
    }
  },

  /**
   * Info-level logging (general information)
   * Displayed in all environments
   *
   * Use for: Key events, successful operations, important milestones
   */
  info(message: string, ...args: any[]): void {
    if (shouldLog('INFO')) {
      console.log(formatPrefix('INFO'), message, ...args);
    }
  },

  /**
   * Warning-level logging (potential issues)
   * Displayed in all environments
   *
   * Use for: Fallback scenarios, degraded functionality, validation warnings
   */
  warn(message: string, ...args: any[]): void {
    if (shouldLog('WARN')) {
      console.warn(formatPrefix('WARN'), message, ...args);
    }
  },

  /**
   * Error-level logging (errors and failures)
   * Always displayed
   *
   * Use for: Exceptions, critical failures, data integrity issues
   */
  error(message: string, ...args: any[]): void {
    if (shouldLog('ERROR')) {
      console.error(formatPrefix('ERROR'), message, ...args);
    }
  },

  /**
   * Configure logger settings
   * Useful for testing or runtime configuration changes
   */
  configure(newConfig: Partial<LoggerConfig>): void {
    config = { ...config, ...newConfig };
  },

  /**
   * Reset logger to default configuration
   */
  reset(): void {
    config = { ...defaultConfig };
  },

  /**
   * Get current configuration
   */
  getConfig(): Readonly<LoggerConfig> {
    return { ...config };
  },
};

/**
 * Tagged logger factory
 * Creates a logger instance with a fixed tag prefix
 *
 * @param tag - Tag to prefix all messages (e.g., "STAGING", "EGRESS")
 * @returns Logger instance with tagged methods
 *
 * @example
 * ```typescript
 * const stagingLogger = createTaggedLogger('STAGING');
 * stagingLogger.debug('Finding staging chain'); // [DEBUG] [STAGING] Finding staging chain
 * ```
 */
export function createTaggedLogger(tag: string) {
  return {
    debug(message: string, ...args: any[]): void {
      if (shouldLog('DEBUG')) {
        console.log(formatPrefix('DEBUG', tag), message, ...args);
      }
    },
    info(message: string, ...args: any[]): void {
      if (shouldLog('INFO')) {
        console.log(formatPrefix('INFO', tag), message, ...args);
      }
    },
    warn(message: string, ...args: any[]): void {
      if (shouldLog('WARN')) {
        console.warn(formatPrefix('WARN', tag), message, ...args);
      }
    },
    error(message: string, ...args: any[]): void {
      if (shouldLog('ERROR')) {
        console.error(formatPrefix('ERROR', tag), message, ...args);
      }
    },
  };
}
